import type { jsPDF } from 'jspdf';
import { colors, fonts } from '../../theme/themes';

export interface ReportFooterOptions {
  x: number;
  width: number;
  pageNumber: number;
  totalPages: number;
  generatedAt: Date;
  organization?: string;
  dataSource?: string;
  confidentialityNotice?: string;
}

function formatTimestamp(date: Date): string {
  const datePart = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const timePart = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
  return `${datePart}, ${timePart}`;
}

/**
 * Draws the footer on the CURRENT page (caller must doc.setPage(n) first).
 * Two lines: a confidentiality notice on its own line (given this report
 * carries district-level data on at-risk IDPs, this earns top billing,
 * not an afterthought), then org/copyright + data source on the left and
 * generation timestamp + page number on the right.
 */
export function drawReportFooter(doc: jsPDF, opts: ReportFooterOptions): void {
  const { x, width, pageNumber, totalPages, generatedAt } = opts;
  const organization = opts.organization ?? '\u00A9 2026 Norwegian Refugee Council';
  const confidentialityNotice =
    opts.confidentialityNotice ??
    'Contains sensitive protection information \u2014 do not distribute without authorization. Findings are based on community-reported information and local monitoring.';

  const pageHeight = doc.internal.pageSize.getHeight();
  const ruleY = pageHeight - 34;
  const line1Y = pageHeight - 22;
  const line2Y = pageHeight - 12;

  // Top rule
  doc.setDrawColor(...colors.stone200);
  doc.setLineWidth(0.5);
  doc.line(x, ruleY, x + width, ruleY);

  // Confidentiality notice
  doc.setFont(fonts.body, 'normal');
  doc.setFontSize(6.5);
  doc.setTextColor(...colors.stone400);
  doc.text(confidentialityNotice, x, line1Y);

  // Left side: organization · data source with clickable link
  doc.setTextColor(...colors.stone400);

  const orgText = `${organization} \u00B7 `;
  doc.text(orgText, x, line2Y);

  const afterOrgX = x + doc.getTextWidth(orgText);

  const prefix = 'For more refer to the online ';
  doc.text(prefix, afterOrgX, line2Y);

  const linkX = afterOrgX + doc.getTextWidth(prefix);
  const linkText = 'somalia evictions';

  // Clickable link (orange to match your theme)
  doc.setTextColor(37, 99, 235); // #2563EB
  doc.textWithLink(linkText, linkX, line2Y, {
    url: 'https://somevictions.app.nrc.no/',
  });

  // Text after the link
  const afterLinkX = linkX + doc.getTextWidth(linkText);
  doc.setTextColor(...colors.stone400);
  doc.text(' portal.', afterLinkX, line2Y);

  // Right side: timestamp + page number
  doc.setTextColor(...colors.stone400);
  const rightText = `Generated ${formatTimestamp(generatedAt)} \u00B7 Page ${pageNumber} of ${totalPages}`;
  const rightWidth = doc.getTextWidth(rightText);
  doc.text(rightText, x + width - rightWidth, line2Y);
}